import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, ShoppingBag } from "lucide-react";
import { useCart } from "@/shop/CartContext";

const WHATSAPP = process.env.REACT_APP_WHATSAPP_NUMBER || "919003140803";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Why Zentra", to: "/#why" },
  { label: "Microgreens", to: "/microgreens" },
  { label: "Shop", to: "/shop" },
  { label: "Contact", to: "/contact" },
];

const smoothScrollTo = (selector) => {
  const el = document.querySelector(selector);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.2 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { count, setOpen: setCartOpen } = useCart();

  const go = (to) => {
    setOpen(false);
    const onHome = location.pathname === "/" || location.pathname === "/meet-zentra";
    if (to.startsWith("/#")) {
      const id = to.slice(1);
      if (onHome) {
        smoothScrollTo(id);
      } else {
        navigate(to);
      }
      return;
    }
    if (to === "/" && onHome) {
      if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.2 });
      else window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    navigate(to);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-[70] border-b border-white/10 bg-[#0A0A0A]/70 backdrop-blur-xl">
      <nav className="mx-auto flex h-[64px] max-w-[1400px] items-center justify-between px-5 lg:px-12">
        <button onClick={() => go("/")} data-testid="site-nav-logo" aria-label="Zentra home">
          <img src="/assets/zentra-logo-white.png" alt="ZENTRA" className="h-8 w-auto" />
        </button>

        <div className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => go(l.to)}
              data-testid={`site-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-xs font-medium uppercase tracking-[0.16em] transition-colors duration-300 hover:text-[#7ED321] ${
                (l.to === "/" && (location.pathname === "/" || location.pathname === "/meet-zentra")) ||
                (l.to !== "/" && !l.to.startsWith("/#") && location.pathname === l.to)
                  ? "text-[#7ED321]"
                  : "text-white/70"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setCartOpen(true)}
            data-testid="site-nav-cart-button"
            aria-label="Open cart"
            className="relative flex h-10 w-10 items-center justify-center border border-white/15 text-white transition-colors duration-300 hover:border-[#7ED321] hover:text-[#7ED321]"
          >
            <ShoppingBag className="h-[18px] w-[18px]" />
            {count > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#7ED321] text-[10px] font-bold text-[#0A0A0A]" data-testid="cart-count-badge">
                {count}
              </span>
            )}
          </button>
          <a
            href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hi Zentra! I'd like to know more / place an order.")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="site-nav-whatsapp"
            className="hidden items-center gap-1.5 rounded-full bg-[#7ED321] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#0A0A0A] transition-colors duration-300 hover:bg-[#8FE331] sm:inline-flex"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            WhatsApp
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="site-nav-menu-button"
            className="flex h-10 w-10 items-center justify-center border border-white/15 text-white lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#0A0A0A]/95 backdrop-blur-xl lg:hidden"
            data-testid="site-nav-mobile-menu"
          >
            <div className="flex flex-col px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.label}
                  onClick={() => go(l.to)}
                  data-testid={`site-nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border-b border-white/5 py-3.5 text-left font-display text-xl uppercase tracking-wide text-white/80 transition-colors duration-300 hover:text-[#7ED321]"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hi Zentra! I'd like to know more / place an order.")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="site-nav-mobile-whatsapp"
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-[#25D366] py-3.5 text-xs font-bold uppercase tracking-[0.12em] text-white"
              >
                <MessageCircle className="h-4 w-4" />
                Order on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default SiteHeader;
